import React, { useContext, useEffect } from 'react'
import { Container } from '@olxbr/ds-web'
import styled from 'styled-components'
import { CheckPinCodeTemplate, SEND_MODES } from '../../templates/CheckPinCodeTemplate'
import { ArrowLeft } from '@olxbr/olx-icons/dist/System'

import PropTypes from 'prop-types'
import { CountdownContext } from '../../../util/providers/CountdownProvider'
import {
  checkCode,
  checkCodev4,
  sendSMS,
  sendSMSv4,
  makeCall,
  makeCallv4,
} from '../../../api/phoneValidationApiCall'
import { sendPinCode as sendPinCodeV4 } from '../../../api/pinCodeApi'

import { breakpoints } from '../../../util/medias'

import Lurker from '../../../util/lurker'
import { getAttemptCheck, getAttemptCheckv2 } from '../../../api/attemptCheckAPI'
import { View } from '../../atoms/View'
import { getModalLoginExtraData } from '../../../util/experiments'
import { isValidMustTypify } from '../../../util/account'
import { MANDATORY_TIPIFICATION_KEY } from '../../../util/global.consts'
import storage from '../../../util/storage'

const lurker = new Lurker('phone_validation')

const Wrapper = styled.div`
  flex: 1;
  flex-direction: column;
  margin: auto;
  @media (max-width: ${breakpoints['mobile']}px) {
    margin-left: var(--spacing-2);
    margin-right: var(--spacing-2);
  }
  @media (min-width: 840px) {
    max-width: 600px;
  }
  @media (min-width: ${breakpoints['mobile'] + 1}px) and (max-width: ${breakpoints['tablet']}px) {
    width: 80vw;
  }
`

const getContentElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Container {...props} />

const ContainerWrapper = styled(getContentElement)`
  @media (max-width: ${breakpoints['mobile']}px) {
    border: none !important;
    padding: 0;
  }
`

const StyledArrow = styled(ArrowLeft)`
  color: var(--color-neutral-130);
`
const ArrowContainer = styled.div`
  cursor: pointer;
  margin-bottom: var(--spacing-4);

  ${({ modalMode }) => modalMode && `margin-bottom: var(--spacing-2);`}
`

const CheckPinCodePage = ({
  onBackClick,
  onSuccess,
  onPhoneConflict,
  phone,
  token,
  from,
  abTests,
  modalMode,
  onAccountCompleteData,
  defaultSendMode,
  isPhoneValidationFlow,
}) => {
  const { lockTime, checkAttemptLockTime } = useContext(CountdownContext)

  /**
   * Handles errors related to PIN code validation and triggers appropriate UI updates and analytics events.
   *
   * @param {Object} [error={}] - The error object returned from the API.
   * @param {string} [eventName='error'] - The name of the event to be logged.
   * @param {string} [pincode=''] - The PIN code entered by the user.
   * @param {Function} [showError=()=>{}] - Callback to display error messages in the UI.
   * @param {Function} [onMaxAccountCheckAttemptReached=()=>{}] - Callback invoked when maximum account check attempts are reached.
   * @param {Function} [updateRemainingAttempts=()=>{}] - Callback to update the number of remaining attempts.
   */
  const onPinCodeError = (
    error = {},
    eventName = 'error',
    pincode = '',
    showError = () => {},
    onMaxAccountCheckAttemptReached = () => {},
    updateRemainingAttempts = () => {}
  ) => {
    const {
      response: {
        status,
        data: {
          message = '',
          current_email: email = '',
          conflicts: emails = [],
          details = { ttl: 0, attempts: 2, max_attempts: 4, remaining_attempts: 2 },
          token,
        } = {},
      } = {},
    } = error

    updateRemainingAttempts(details.remaining_attempts)

    let genericError = 'showGenericError'
    if (eventName !== 'error') {
      genericError = 'showResendCodeError'
    }

    if (status === 401) {
      if (message === 'ACCOUNT_TYPING_REQUIRED') {
        lurker.viewMessageEvt('error', {
          accounts_reason: message.toLowerCase(),
          accounts_status_code: status,
          login_type: 'phone_validation',
          flow: error.flow || 'phone_validation',
          type: error.pincode_method || SEND_MODES.SMS.toLowerCase(),
        })

        storage.set(MANDATORY_TIPIFICATION_KEY, true)

        if (onAccountCompleteData) {
          onAccountCompleteData(token)
        }
      } else {
        lurker.viewMessageEvt(eventName, {
          accounts_reason: 'unauthorized',
          accounts_status_code: status,
          flow: error.flow ? error.flow : 'phone_validation',
          type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
        })
        showError(genericError)
      }
    } else if (status === 429) {
      if (message === 'MAX_PHONE_VALIDATION_ATTEMPTS_REACHED') {
        lurker.viewMessageEvt(eventName, {
          accounts_reason: 'max_phone_validation',
          accounts_status_code: status,
          flow: error.flow ? error.flow : 'phone_validation',
          type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
        })
        showError('showMaxPhoneValidationError')
      } else if (message === 'MAX_ATTEMPTS_CHECK_PIN_CODE') {
        lurker.viewMessageEvt(eventName, {
          accounts_reason: 'max_account_check_attempts',
          accounts_status_code: status,
          flow: error.flow ? error.flow : 'phone_validation',
          type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
        })
        onMaxAccountCheckAttemptReached(details.ttl)
        showError('showMaxAccountCheckAttempts')
      } else {
        lurker.viewMessageEvt(eventName, {
          reason: 'rate_limit_exceeded',
          accounts_status_code: status,
          flow: error.flow ? error.flow : 'phone_validation',
          type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
        })
        showError('showRateLimitError')
      }
    } else if (status === 403) {
      switch (message) {
        case 'PHONE_IN_BLACKLIST':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'phone_number_in_blacklist',
            accounts_status_code: status,
            flow: error.flow ? error.flow : 'phone_validation',
            type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
          })
          showError('showPhoneInBlackListError')
          break
        case 'PHONE_IN_USE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'phone_number_already_in_use',
            accounts_status_code: status,
            flow: error.flow ? error.flow : 'phone_validation',
            type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
          })
          onPhoneConflict(email, emails, pincode)
          break
        case 'INVALID_PIN_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'invalid_pin_code',
            accounts_status_code: status,
            flow: error.flow ? error.flow : 'phone_validation',
            type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
          })
          showError('showInvalidPinCodeError')
          break
        case 'NO_PIN_CODE':
          lurker.viewMessageEvt(eventName, {
            accounts_reason: 'no_pin_code',
            accounts_status_code: status,
            flow: error.flow ? error.flow : 'phone_validation',
            type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
          })
          showError(genericError)
          break
        default:
          lurker.viewMessageEvt(eventName, {
            accounts_reason:
              message && typeof message === 'string'
                ? message.toLowerCase()
                : 'unexpected_status_code',
            accounts_status_code: status,
            flow: error.flow ? error.flow : 'phone_validation',
            type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
          })
          showError(genericError)
      }
    } else if (status) {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'unexpected_status_code',
        accounts_status_code: status,
        accounts_error_message: (error && error.message) || 'unknown',
        flow: error.flow ? error.flow : 'phone_validation',
        type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
      })
      showError(genericError)
    } else {
      lurker.viewMessageEvt(eventName, {
        accounts_reason: 'unexpected_error',
        accounts_error_message: (error && error.message) || 'unknown',
        flow: error.flow ? error.flow : 'phone_validation',
        type: error.pincode_method ? error.pincode_method : SEND_MODES.SMS.toLowerCase(),
      })
      showError(genericError)
    }
  }

  const checkAttempt = () => (token ? getAttemptCheckv2(token) : getAttemptCheck())

  useEffect(() => {
    checkAttemptLockTime(checkAttempt)
  }, [])

  const sendSMSCall = token ? phone => sendSMSv4(phone, token) : sendSMS
  const makePhoneCall = token ? phone => makeCallv4(phone, token) : makeCall

  const sendPinCodeCall = type => {
    return sendPinCodeV4(phone, type, token)
  }

  const checkCodeCall = async (phone, code) => {
    const apiCall = token ? checkCodev4(phone, code, token, abTests) : checkCode(phone, code)

    const isMustTypify = await isValidMustTypify()
    const isValidTypify = from === 'login' && typeof onAccountCompleteData === 'function'

    return apiCall.then(response => {
      const { publicAccountId, isTypified } = response
      lurker.setPublicAccountId(publicAccountId)
      const extraData = getModalLoginExtraData(abTests, modalMode)

      lurker.viewMessageEvt('success', {
        accountId: response.accountsId,
        ...extraData,
      })

      if (isValidTypify && !isTypified && isMustTypify) {
        onAccountCompleteData()

        return
      }

      return response
    })
  }

  return (
    <Wrapper>
      <ArrowContainer modalMode={modalMode} onClick={onBackClick}>
        <StyledArrow />
      </ArrowContainer>

      {!modalMode && <span className="typo-title-large mb-4 block">Estamos quase lá</span>}
      {modalMode && <span className="typo-title-medium mb-1 block">Estamos quase lá</span>}

      <ContainerWrapper
        modalMode={modalMode}
        type="outlined"
        paddingBottom={!modalMode && '--spacing-8'}
        paddingTop={!modalMode && '--spacing-8'}
        paddingLeft={!modalMode && '--spacing-8'}
        paddingRight={!modalMode && '--spacing-8'}
      >
        <CheckPinCodeTemplate
          checkCode={checkCodeCall}
          sendPinCode={sendPinCodeCall}
          sendSMS={sendSMSCall}
          defaultSendMode={defaultSendMode || SEND_MODES.WHATSAPP}
          makeCall={makePhoneCall}
          onResend={() => checkAttemptLockTime(checkAttempt)}
          onSuccess={onSuccess}
          onPinCodeError={onPinCodeError}
          phone={phone}
          lockTime={lockTime}
          lurker={lurker}
          token={token}
          from={from}
          modalMode={modalMode}
          abTests={abTests}
          isPhoneValidationFlow={isPhoneValidationFlow}
        />
      </ContainerWrapper>
    </Wrapper>
  )
}

CheckPinCodePage.propTypes = {
  onBackClick: PropTypes.func.isRequired,
  onResend: PropTypes.func,
  onSuccess: PropTypes.func,
  onPhoneConflict: PropTypes.func,
  phone: PropTypes.string,
  token: PropTypes.string,
  from: PropTypes.string,
  modalMode: PropTypes.bool,
  onAccountCompleteData: PropTypes.func,
  defaultSendMode: PropTypes.string,
  isPhoneValidationFlow: PropTypes.bool,
}

CheckPinCodePage.defaultProps = {
  isPhoneValidationFlow: false,
}

export { CheckPinCodePage, SEND_MODES }
